/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useCallback } from 'react';
import { Text as NativeText, ITextProps, IBoxProps, Box } from 'native-base';

import { I18nReacher, typographyRegex, useI18n } from '@springbig/shared';

import { nullDefault } from '~/utils/misc';

export interface TypographyProps extends ITextProps {
    text?: I18nReacher;
    textValues?: string[];
    testID?: string;
}

const Typography = ({ text, textValues, children, ...props }:TypographyProps) => {
    const { t } = useI18n();

    const renderText = useCallback((text, textValues) => {
        const str = t(text, textValues || undefined);
        const elements = [];

        let currentWeight = props.fontWeight || 'normal';
        let currentItalic = false;
        const parts = str.split(typographyRegex).filter(Boolean);
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];

            if (part === '{b}') {
                currentWeight = 'bold';
            } else if (part === '{/b}') {
                currentWeight = props.fontWeight || 'normal';
            } else if (part === '{i}') {
                currentItalic = true;
            } else if (part === '{/i}') {
                currentItalic = false;
            } else if (part.startsWith('{c:')) {
                const customWeightMatch = part.match(/\{c:(\d+)\}/);
                if (customWeightMatch) {
                    currentWeight = customWeightMatch[1];
                }
            } else if (part.startsWith('{/c:')) {
                currentWeight = props.fontWeight || 'normal';
            } else {
                elements.push(
                    <NativeText
                        key={i}
                        {...props}
                        fontWeight={currentWeight}
                        fontStyle={currentItalic ? 'italic' : 'normal'}
                    >
                        {part}
                    </NativeText>
                );
            }
        }

        return elements;
    },[text, textValues]);

    return (
        <NativeText {...props} bg="transparent">
            {text ? renderText(text, textValues) : children}
        </NativeText>
    );
};

export const H1 = ({ text, textValues, ...props }: TypographyProps) => (

    <Typography
        text={text}
        textValues={textValues}
        my={3}
        fontWeight="bold"
        fontSize={20}
        {...props}
    />
);

export const H2 = ({ text, textValues, ...props }: TypographyProps) => (

    <Typography
        text={text}
        textValues={textValues}
        my={3}
        fontWeight="bold"
        fontSize={18}
        {...props}
    />
);

export const H3 = ({ text, textValues, ...props }: TypographyProps) => (

    <Typography
        text={text}
        textValues={textValues}
        fontWeight="bold"
        fontSize={16}
        {...props}
    />
);

export const H4 = ({ text, textValues, ...props }: TypographyProps) => (

    <Typography
        text={text}
        textValues={textValues}
        fontWeight="bold"
        fontSize={14}
        {...props}
    />
);

export const Caption = ({ text, textValues, ...props }: TypographyProps) => (

    <Typography
        text={text}
        textValues={textValues}
        fontWeight="normal"
        fontSize={14}
        {...props}
    />
);

export const Txt = (props: TypographyProps) => <Caption {...props} />;

export const CaptionError = ({ text, textValues, ...props }: TypographyProps) => (

    <Typography
        text={text}
        textValues={textValues}
        fontWeight="normal"
        fontSize={14}
        color='danger'
        {...props}
    />
);

export const Hr = (props: IBoxProps) => {

    return <Box
        borderWidth={2}
        borderStyle={'solid'}
        borderColor={'light'}
        w={'100%'}
        my={3}
        minH={1}
        {...props}
        borderTopWidth={0}
        borderLeftWidth={0}
        borderRightWidth={0}
        // eslint-disable-next-line react/no-children-prop
        children={null}
    />;
};

export default Typography;
